import _ from "lodash";

import {Service} from "./service";
import {
  getProductSelectionStepData,
  instrumentDataPath,
  productsStepsData,
  SAVINGS_PLANS_KEY, SINGLE_INVESTMENTS_KEY
} from "./mock_produktauswahl"

import {
  _prepareBanksFromResponse, getBankData,
  getCustodianData,
  getCustomerData,
  getTargetMarketAnswers,
  getVatCoef, splitEinzeltitelTradingInstruments
} from './utils';
import {
  BankSettingsHandlerResource,
  ModelPortfolioResource,
  parseResponse,
  QuestionnairesHandlerResource
} from '../../utils/api'
import {getBookingDate, getInstrumentName} from "../Trades/utils";
import {
  INSTRUMENT_SOURCE_TYPE,
  PROCESS_TYPE,
  VALID_UNTIL_TODAY_VALUE
} from './constants';
import {SERVICE_CONCEPTS} from "../Trades/constants";
import {getIndividualAssetManagementSteps} from "./components/servicesV2/mock_product_selection";

export class ProductsService extends Service {

  constructor(uid, name, next_btn, success_text, getPrevStepAnswer, validateAccountFunc, handleWarnings) {
    super(uid, name, next_btn, success_text, getPrevStepAnswer, validateAccountFunc, handleWarnings);

    this._srri = undefined;
    this._target_market = undefined;
    this._custodiansInstrumentsData = undefined;
    this._banksData = undefined;
    this._service_concept = undefined;
    this.skipStepsIfOpenOnly = [
      "product-selection-investment-recommendation",
      "product-selection-summary"
    ]
    this.__skipStepsIfIndividualAssetManagement = [];
    this.__stepsToValidate = ['product-selection-investment-recommendation', 'product-selection-deposits']
  }

  get serviceConcept () {
    return this._service_concept
  }

  set serviceConcept (value) {
    this._service_concept = value
  }

  get openOnlyProductSelectionRequired() {
    const depot = this.getDepot();
    const bankData = getBankData(this.banksData, depot.bankCompanyId);

    return bankData.onboarding_open_only_product_selection_required
  }

  // TODO: we should refactor bank config usage after new design
  getBankConfig(depot){
    // if we have depot selected - override with config from depot type
    depot = depot || this.getDepot(true);
    const bankConfig = depot && this.banksDataGrouped[depot.bankCompanyId];
    return {...bankConfig, ..._.get(depot, 'selected_contract_type.configuration')};
  }

  async __sendStepData() {
    let isOpenOnly = this.isOpenOnly;

    if (this.__stepsToValidate.includes(this._step.uid) || (!this.isNewDesign && isOpenOnly)) {
      try {
        await this._validateStepFunc();
      } catch (e) {
        // ignore errors on deposits step -> show it later
        if (this._step.uid !== 'product-selection-deposits') {
          throw e;
        }
      }
      // get instruments custodian details
      await this._getInstrumentsCustodiansDetails();
    }

    let response = await super.__sendStepData(); // store answers in our db

    if (isOpenOnly && this._step.uid === this.__depotOpeningStepUID) {
      await this.fillWithDefaultInstruments()
    }

    // recalculate _isLastStep
    await this.__setStep(0);

    return response
  }

  /**
   * Fill instruments question with default answers according to the depot configuration
   * @returns {Promise<void>}
   */
  async fillWithDefaultInstruments() {
    const depot = this.getDepot();

    if (!depot) {
      return
    }

    const bankData = getBankData(this.banksData, depot.bankCompanyId);
    const defaultInstrument = _.get(bankData, 'onboarding_open_only_default_instrument')
    if (!defaultInstrument) {
      return
    }

    const instrumentsResponse = await ModelPortfolioResource.searchAssets({
      name: defaultInstrument,
      custodian: depot.bankCompanyId
    });

    parseResponse(instrumentsResponse, 'assets', (data) => {

      const instrumentOpenOnlyInfoText = _.get(bankData, 'onboarding_open_only_default_instrument_info_text')

      this.getQuestion('product-selection-investment-recommendation', 'instruments').answer = data.map((asset) => {
        asset.weight = 0;
        asset.amount = 0;
        asset.savingsPlanAmount = 0;
        asset.infoText = instrumentOpenOnlyInfoText
        return asset
      })
    }, (errors) => {
      console.log(errors)
    });
  }

  removeInstrumentsSteps() {
    this.__removeSteps(this.skipStepsIfOpenOnly);
  }

  addInstrumentsSteps() {
    this.__addSteps(this.skipStepsIfOpenOnly, this.__depotOpeningStepUID);
  }

  individualAssetManagementMountSteps() {
    this.__removeSteps(this.__skipStepsIfIndividualAssetManagement);
    this._questionnaire.steps.push(...getIndividualAssetManagementSteps())
  }

  individualAssetManagementUnmountSteps() {
    this.__removeSteps(getIndividualAssetManagementSteps().map(s => s.uid))
    this.__addSteps(
      this.__skipStepsIfIndividualAssetManagement,
      this.isNewDesign ? 'product-selection-depot-type' : 'product-selection-depot'
    );
  }

  __addSteps(stepsToAdd, insertAfterUID) {
    let stepsUIDs = this._questionnaire.steps && this._questionnaire.steps.map(item => item && item.uid);
    let indexForInsert = stepsUIDs.indexOf(insertAfterUID) + 1;
    let stepsCopy = this.__getSteps().steps;
    stepsToAdd.forEach(step => {
      if (!stepsUIDs.includes(step))
      this._questionnaire.steps.splice(indexForInsert, 0, stepsCopy.find(s => s.uid === step));
      indexForInsert += 1
    })
  }

  __removeSteps(stepsToRemove) {
    this._questionnaire.steps = this._questionnaire.steps.filter(s => {
      return !stepsToRemove.includes(s.uid)
    })
  }

  async _getInstrumentsCustodiansDetails() {
    const instruments = this.getStepAnswer('product-selection-investment-recommendation', 'instruments') || [];
    const assetsData = await getCustodianData(instruments);
    if(assetsData) this._custodiansInstrumentsData = assetsData;
  }

  async _getBanksData() {
    try {
      let response = await BankSettingsHandlerResource.getBanksData({
        customer_type: this.customer_type,
        usage_area: PROCESS_TYPE.ONBOARDING
      });
      return _prepareBanksFromResponse(response)

    } catch (err) {
      console.log(err);
      return undefined
    }
  }

  getDepot(withContractTypeData=false) {
    const answer = this.getStepAnswer('product-selection-depot', 'custodians');

    const depot = answer && answer.depot;
    if (depot && withContractTypeData) {
      depot.selected_contract_type = depot.contract_types.find(
        contract => contract.value === answer.contract_type);
    }

    return depot
  }

  get hasPrivateInvestmentDepots() {

    const custodiansQuestion = this.getQuestion('product-selection-depot', 'custodians');
    for(let custodian of (custodiansQuestion.options || [])) {
      if(custodian.is_private_investment) {
        return true
      }
    }

  }

  get bankCompanyId(){
    const depot = this.getDepot();
    return depot && depot.bankCompanyId;
  }

  get banksData(){
    let banksData = this._banksData || [];

    // if we have depot selected - override with config from depot type
    const depot = this.getDepot(true);
    if (depot && depot.selected_contract_type){
      // clone bankData to keep original
      banksData = banksData.map(b => {
        if(b.custodian_id == depot.bankCompanyId){
          b = {...b, ...depot.selected_contract_type.configuration}
        }

        return b;
      });
    }

    return banksData;
  }

  _getBank() {
    const depot = this.getDepot();
    return depot && depot.bank;
  }

  getBankInstrumentsInfo(bank) {
    return this._custodiansInstrumentsData && this._custodiansInstrumentsData[bank] || {};
  }

  getFundsBuyTotal(){
    let nonFunds = [], funds = [];
    let instruments = this.getStepAnswer('product-selection-investment-recommendation', 'instruments') || [];
    [nonFunds, funds] = splitEinzeltitelTradingInstruments(instruments, {}, nonFunds, funds);

    return this.__getTotal(funds.filter(i => !i.savingsPlan));
  }

  __getTotal(items){
    return _.round(items.reduce((accumulator, item) => (item.amount || item.transaction_value || 0) + accumulator, 0), 2);
  }

  getInvestedAmountData() {
    const savingsPlanEnabled = this._isSavingsPlansEnabled();
    const singleInvestmentEnabled = this._isSingleInvestmentEnabled();
    const vlPlanEnabled = this._isVLEnabled();
    const einzeltitelEnabled = this.isEinzeltitelEnabled();

    let savingsPlanAmount, savingsPlanPeriod;
    if(vlPlanEnabled){
      savingsPlanAmount = this.getStepAnswer("product-selection-deposits", "vl_plan['input']");
      savingsPlanPeriod = this.getStepAnswer("product-selection-deposits", "vl_plan['rotation']")
    } else if (savingsPlanEnabled){
      savingsPlanAmount = this.getStepAnswer("product-selection-deposits", "savings_plan['input']");
      savingsPlanPeriod = this.getStepAnswer("product-selection-deposits", "savings_plan['rotation']")
    }

    return {
      single_investment_enabled: singleInvestmentEnabled,
      single_investment: singleInvestmentEnabled && this.getStepAnswer("product-selection-deposits", "single_investment['input']") || 0,
      savings_plan: savingsPlanAmount || 0,
      savings_plan_enabled: savingsPlanEnabled,
      savings_plan_period: savingsPlanPeriod,
      vl_plan_enabled: vlPlanEnabled,
      einzeltitel_enabled: einzeltitelEnabled
    }
  }

  getDataForAccount(validate, forExAnte=false) {
    const modelPortfolio = this.getStepAnswer('product-selection-investment-recommendation', 'instruments_source');
    const bank = this._getBank();
    const depot = this.getDepot();
    const availableAssetClasses = this.bank && this.bank.available_asset_classes || {};
    const useAssetSettings = this.isNewDesign && !modelPortfolio;

    let savingsPlanStartDate, savingsPlanEndDate, periodicPlan, singleBuyStartDate;
    if(this.isNewDesign && modelPortfolio){
      savingsPlanStartDate = _.get(modelPortfolio, 'savingPlanData.from_date');
      savingsPlanEndDate = _.get(modelPortfolio, 'savingPlanData.till_date');
      periodicPlan = _.get(modelPortfolio, 'savingPlanData.rotation.value');
      singleBuyStartDate = getBookingDate(modelPortfolio);
    } else {
      savingsPlanStartDate = this.getStepAnswer("product-selection-deposits", "savings_plan['from_date']");
      savingsPlanEndDate = this.getStepAnswer("product-selection-deposits", "savings_plan['till_date']");
      periodicPlan = this.getStepAnswer("product-selection-deposits", "savings_plan['rotation']");
      singleBuyStartDate = this.getStepAnswer("product-selection-deposits", "single_investment['from_date']");
    }

    const vlPlanStartDate = this.getStepAnswer("product-selection-deposits", "vl_plan['from_date']");
    const vlPlanPeriodicPlan = this.getStepAnswer("product-selection-deposits", "vl_plan['rotation']");

    const data = {bank: bank, bank_id: depot && depot.bankId, advisor_details: {}};

    const discountsInfo = this.getStepAnswer("product-selection-summary", "discounts") || {};
    const combinedDiscounts = _.get(this.banksDataGrouped[depot.bankCompanyId], 'combined_discounts');
    function getDiscount(instrument) {
      if (discountsInfo.discountsEnabled) {
        const instrumentData = _.get(discountsInfo, instrumentDataPath(instrument), {});
        let discount = parseInt(instrumentData.discount) || 0;
        if(forExAnte && combinedDiscounts && discountsInfo.overall) {
          discount += parseInt(discountsInfo.overall)
        }
        return discount || undefined;
      }
    }

    if (modelPortfolio && !forExAnte){
      const investedData = this.isNewDesign ? this.getPortfolioInvestedData(modelPortfolio) : this.getInvestedAmountData();

      const instrument_type = modelPortfolio.isInvestmentStrategy ? 'model portfolio managed' : 'model portfolio';
      const dst_instrument = modelPortfolio.isInvestmentStrategy ? modelPortfolio.portfolio_id : modelPortfolio.agency_portfolio_id;

      function getMPDiscount(key) {
        if (discountsInfo.discountsEnabled) {
          // in case discount is not set ("", 0, etc) - return undefined as it won't be sent with axios
          return _.get(discountsInfo, key) || undefined;
        }
      }

      if(investedData.single_investment_enabled || this.isOpenOnly) {
        data.order_details = [{
          instrument_type,
          dst_instrument,
          dst_instrument_name: modelPortfolio.name.slice(0, 120),
          amount_eur: investedData.single_investment,
          bank: bank,
          discount: getMPDiscount(SINGLE_INVESTMENTS_KEY),
          booking_date: singleBuyStartDate || undefined
        }]
      }
      if(investedData.savings_plan_enabled){

        const _saving_plan_structure = {
          instrument_type,
          dst_instrument,
          dst_instrument_name: modelPortfolio.name.slice(0, 120),
          amount_eur: investedData.savings_plan,
          bank: bank,
          start_date: savingsPlanStartDate,
          periodic_plan: periodicPlan,
          discount: getMPDiscount(SAVINGS_PLANS_KEY)
        };
        if (savingsPlanEndDate) {
          _saving_plan_structure.end_date = savingsPlanEndDate
        }

        data.savings_plans = [_saving_plan_structure]
      }
    } else {
      let instruments = this.getStepAnswer('product-selection-investment-recommendation', 'instruments') || [];
      const vlPlanEnabled = this._isVLEnabled();

      function getTotalAmount(instrument){
        return _.round(instrument.amount || 0, 2);
      }

      data.order_details = [];
      instruments.filter(i => !i.savingsPlan).forEach((instr) => {
        const order = {
          dst_instrument: instr.isin,
          dst_instrument_name: getInstrumentName(instr),
          amount_eur: getTotalAmount(instr),
          booking_date: (useAssetSettings ? getBookingDate(instr) : singleBuyStartDate) || undefined,
          bank: bank
        };
        if (_.get(availableAssetClasses, instr.internal_asset_class)) {
          order.instrument_type = `isin ${instr.internal_asset_class}`;
          order.quantity = instr.quantity;
          order.order_type = instr.order_type !== 'no order' ? instr.order_type : undefined;
          order.limit_amount_eur = instr.limit || 0;
          if (instr.valid_until === VALID_UNTIL_TODAY_VALUE) {
            order.valid_until_today = true;
          } else {
            order.valid_until = instr.valid_until;
          }
          order.stock_exchange = instr.stock_exchange;
        } else if (order.amount_eur) {
          order.discount = getDiscount(instr)
        }
        data.order_details.push(order)
      });

      const savings_plans_key = !vlPlanEnabled ? 'savings_plans' : 'vl_plan_details';

      if (!this.isNewDesign && vlPlanEnabled && _.isEmpty(instruments)){
        // put dummy item to validate vl amount
        instruments = [{
          savingsPlan: true,
          isin: "DE1234567891",
          amount: this.getStepAnswer("product-selection-deposits", "vl_plan['input']")
        }]
      }
      data[savings_plans_key] = instruments.filter(i => i.savingsPlan).map(instr => {
        const _savings_plan_structure = {
          // TODO dst_insrument is depracated and can be removed(sparplans)
          dst_instrument: instr.isin,
          dst_instrument_name: getInstrumentName(instr),
          //
          instrument: instr.isin,
          instrument_name: getInstrumentName(instr),
          amount_eur: getTotalAmount(instr),
          bank: bank,
          start_date: useAssetSettings ? instr.savingPlanData.from_date : vlPlanEnabled ? vlPlanStartDate : savingsPlanStartDate,
          periodic_plan: useAssetSettings ? instr.savingPlanData.rotation.value : vlPlanEnabled ? vlPlanPeriodicPlan : periodicPlan,
          discount: getDiscount(instr)
        };
        if (savingsPlanEndDate || useAssetSettings) {
          _savings_plan_structure.end_date = useAssetSettings ? instr.savingPlanData.till_date : savingsPlanEndDate
        }
        return _savings_plan_structure
      });

      // savings_plans is required property
      if (!data.hasOwnProperty('savings_plans')) {
        data.savings_plans = []
      }
    }

    if (this.isNewDesign && modelPortfolio && modelPortfolio.baseFondTransaction){
      let instrument_type, dst_instrument, dst_instrument_name;
      if (forExAnte){
        instrument_type = 'isin';
        dst_instrument = modelPortfolio.baseFondTransaction.isin;
        dst_instrument_name = getInstrumentName(modelPortfolio.baseFondTransaction);

      } else {
        instrument_type = modelPortfolio.isInvestmentStrategy ? 'model portfolio managed base' : 'model portfolio base';
        dst_instrument = modelPortfolio.isInvestmentStrategy ? modelPortfolio.portfolio_id : modelPortfolio.agency_portfolio_id;
        dst_instrument_name = modelPortfolio.name.slice(0, 120)
      }

      if(modelPortfolio.baseFondTransaction.amount > 0){
        data.order_details = data.order_details || [];
        data.order_details.push({
          instrument_type: instrument_type,
          dst_instrument: dst_instrument,
          dst_instrument_name: dst_instrument_name,
          amount_eur: _.round(modelPortfolio.baseFondTransaction.amount, 2),
          bank: bank,
          discount: getDiscount(modelPortfolio.baseFondTransaction),
          booking_date: getBookingDate(modelPortfolio.baseFondTransaction) || undefined
        })
      }
      if(modelPortfolio.baseFondTransaction.savingsPlanAmount > 0){
        data.savings_plans = data.savings_plans || [];
        data.savings_plans.push({
          instrument_type,
          dst_instrument,
          dst_instrument_name,
          amount_eur: _.round(modelPortfolio.baseFondTransaction.savingsPlanAmount, 2),
          bank,
          start_date: modelPortfolio.baseFondTransaction.savingPlanData.from_date,
          end_date: modelPortfolio.baseFondTransaction.savingPlanData.till_date,
          periodic_plan: modelPortfolio.baseFondTransaction.savingPlanData.rotation.value,
          discount: getDiscount({...modelPortfolio.baseFondTransaction, savingsPlan: true})
        })
      }
    }

    let serviceFee = this.getStepAnswer("product-selection-summary", "serviceFee");
    const kickback = this.getStepAnswer("product-selection-summary", "kickback");
    let serviceFeeVat = this.getStepAnswer("product-selection-summary", "serviceFeeVat");
    const isMPProcess = !!modelPortfolio;

    if (_.get(modelPortfolio, 'isInvestmentStrategy')){
      serviceFee = _.sum([
        _.get(modelPortfolio, 'bfv_commission'),
        _.get(modelPortfolio, 'asset_manager_commission'),
        serviceFee
      ]);

      serviceFeeVat = serviceFee * getVatCoef()
    }

    if (discountsInfo.discountsEnabled && discountsInfo.overall) {
      data.advisor_details = {account_discount: discountsInfo.overall};
    }

    if(serviceFee){
      data.advisor_details.service_fee = serviceFee
    }
    if(kickback){
      data.advisor_details.kickback = kickback
    }
    if(serviceFeeVat){
      data.advisor_details.service_fee_vat = serviceFeeVat
    }
    if(isMPProcess){
      data.advisor_details.mp_entry_fee = parseFloat(modelPortfolio.isInvestmentStrategy ?
        modelPortfolio.cost_netto : modelPortfolio.fee_netto || 0);

      data.advisor_details.mp_entry_fee_vat = parseFloat(modelPortfolio.isInvestmentStrategy ?
        modelPortfolio.cost_brutto : modelPortfolio.fee_brutto || 0);
    }

    // CIOS require rounding to 2/5 decimals
    ['mp_entry_fee', 'mp_entry_fee_vat', 'service_fee', 'service_fee_vat'].forEach(field => {
      const advisorDetails = data.advisor_details;

      if (advisorDetails.hasOwnProperty(field)){
        advisorDetails[field] = _.round(advisorDetails[field], field.endsWith('vat') ? 5 : 2);
      }
    });

    return data;
  }

  async syncDepotTypeInfo() {

    const answer = this.getStepAnswer('product-selection-depot', 'custodians');
    if (!answer || !answer.contract_type || !answer.depot) {
      return
    }

    try {

      const response = await BankSettingsHandlerResource.getDepotTypeInfo(answer.contract_type, this.objectType);

      const contractTypes = answer.depot.contract_types || [];
      contractTypes.forEach((contractType) => {
        if (contractType.value == response.id) {
          // For now only depot type configuration synced.
          contractType.configuration = response.configuration
        }
      });
      this.getStep('product-selection-depot', 'custodians').answer = answer
      await this._getBankDetails()

    } catch (error) {
      console.error(`Unable to sync depot type info.\nError: ${error}`)
    }

  }

  async __setQuestionnaire(onboardingAnswers) {
    await super.__setQuestionnaire(onboardingAnswers);
    this.current_customer = _.assign(this.current_customer, await getCustomerData(this._customer_id));
    this.__skipStepsIfIndividualAssetManagement =
      this.isNewDesign ?
        [
          "product-selection-depot-opening-type",
          "product-selection-investment-recommendation",
          "product-selection-summary",
        ] :
        [
          "product-selection-deposits",
          "product-selection-investment-recommendation",
          "product-selection-summary",
          "congrats-step"
        ];
    this._questionnaire = this.__getSteps();
    this._questionnaire.steps.push(...getIndividualAssetManagementSteps())
    this._setDefaultAnswers(onboardingAnswers);

    this._srri = this._getPrevStepAnswer('risk_profile', 'final-step', 'final_question_uid');
    this._target_market = getTargetMarketAnswers(this.serviceConcept, this._getPrevStepAnswer, this.isNewDesign);

    if(!this._custodiansInstrumentsData){
      await this._getInstrumentsCustodiansDetails();
    }

    // TODO: refactor two getBanksData requests into one
    // TODO: replace _banksData with _banksDataGrouped
    if(!this._banksData){
      const res = await QuestionnairesHandlerResource.getBanksData();
      this._banksData = res.response;
      if(!_.isEmpty(this._banksData)){
        this._banksDataGrouped = _.keyBy(this._banksData, 'custodian_id');
      }
    }

    const banksData = await this._getBanksData();
    if (banksData) {
      if(this.isPortfolioBuilderFlow){

        banksData.forEach(bank => {
          let hasAvailableForPBDepotType = false;

          bank.contract_types.forEach(depotType => {
            if(_.get(depotType.configuration, 'instrument_sources', []).includes(INSTRUMENT_SOURCE_TYPE.PORTFOLIO_BUILDER)){
              hasAvailableForPBDepotType = true;
            } else {
              depotType.disabled = true;
              depotType.info = 'Für diesen Depottyp steht der Portfolio Builder nicht zur Verfügung.';
            }
          });

          if(!hasAvailableForPBDepotType){
            bank.disabled = true;
            bank.disabledReason = 'Der Portfolio Builder wird von dieser Depotstelle nicht unterstützt.'
          }
        })
      }

      const depotsQuestion = this.getQuestion('product-selection-depot', 'custodians');
      depotsQuestion.options = banksData;

      if(this.isNewDesign) {
        let hasAvailableForIAMDepotType = false;

        banksData.forEach(bank => {
          const bankConfig = this.banksDataGrouped[bank.bankCompanyId];
          bank.contract_types.forEach(contract => {
            const contractConfig = {...bankConfig, ...contract.configuration};
            const availableInstrumentsSources = contractConfig.instrument_sources || [];
            const fundsDisabled = contractConfig.onboarding_funds_disabled;

            contract.einzeltitelEnabled = contractConfig.einzeltitel_onboarding_enabled;
            contract.singleInvestEnabled = !fundsDisabled && contractConfig.onboarding_order_possible
            contract.isMPEnabled = availableInstrumentsSources.includes(INSTRUMENT_SOURCE_TYPE.MODEL_PORTFOLIO);
            contract.isVLEnabled = !fundsDisabled && contractConfig.onboarding_vl_plan_allowed;
            contract.isPBEnabled = availableInstrumentsSources.includes(INSTRUMENT_SOURCE_TYPE.PORTFOLIO_BUILDER);
            contract.isIndividualAssetManagementEnabled = _.get(contractConfig, 'individual_asset_managment_config.enabled');
            if(!hasAvailableForIAMDepotType && contract.isIndividualAssetManagementEnabled){
              hasAvailableForIAMDepotType = true;
            }
          })
        })

        this._depotsDataGrouped = _.keyBy(banksData, 'bankCompanyId');
        const mpCheckbox = this.getQuestion('product-selection-deposits', 'mp[\'checkbox\']');
        const piCheckbox = this.getQuestion('product-selection-deposits', 'pi[\'checkbox\']');
        if(this.serviceConcept === SERVICE_CONCEPTS.ExecutionOnly){
          mpCheckbox.disabled = piCheckbox.disabled = true;
          piCheckbox.disabledReason = 'Sie können nur eine Vermögensverwaltung starten, wenn Sie eine Anlageberatung oder Anlagevermittlung durchführen.';
          mpCheckbox.disabledReason = 'Modellportfolio steht nur bei Anlageberatung oder -vermittlung zur Verfügung.';
        } else {
          const res = await ModelPortfolioResource.getModelPortfoliosList({is_modelportfolio: true, with_istruments_data: false})
          if(_.isEmpty(res)){
            mpCheckbox.disabled = true;
            mpCheckbox.disabledReason = 'Sie haben noch kein Modellportfolio angelegt.';
          }
        }

        const iamCheckbox = this.getQuestion('product-selection-deposits', "individual_asset_management['checkbox']")
        if(this.serviceConcept !== SERVICE_CONCEPTS.Anlageberatung){
          iamCheckbox.disabled = true;
          iamCheckbox.disabledReason = 'Sie können nur eine Individuelle Vermögensverwaltung starten, wenn Sie eine Anlageberatung durchführen.'
        }
        iamCheckbox._ignore = !hasAvailableForIAMDepotType && !iamCheckbox.answer;

        this.initCustodianChoices(depotsQuestion);
      }
    }

    if(this.isIndividualAssetManagementProcess()){
      this.__removeSteps(this.__skipStepsIfIndividualAssetManagement);
    } else {
      this.individualAssetManagementUnmountSteps()

      if (this.isOpenOnly && !this.openOnlyProductSelectionRequired) {
        this.removeInstrumentsSteps()
      } else {
        if (!this.isNewDesign) {
          // update Product selection step name and category
          const step = this.getStep('product-selection-investment-recommendation');
          const {name, category} = getProductSelectionStepData(this.serviceConcept);
          step.name = name;
          step.category = category;
        }
        if (this.isOpenOnly && !this.getQuestion('product-selection-investment-recommendation', 'instruments').answer) {
          await this.fillWithDefaultInstruments()
        }
      }
    }

    await this.syncDepotTypeInfo()
  }

  async confirmResult(data) {
    this._is_finished = true;
  }

  __getSteps() {
    return {
      steps: this.__deepCopy(productsStepsData)
    }
  }

  get __depotOpeningStepUID(){
    return this.isNewDesign ? 'product-selection-depot-opening-type' : 'product-selection-deposits';
  }

  _isVLEnabled(){
    return this.getStepAnswer("product-selection-deposits", "vl_plan['checkbox']");
  }

  _isSingleInvestmentEnabled(){
    return this.getStepAnswer("product-selection-deposits", "single_investment['checkbox']")
  }

  _isPortfolioBuilderEnabled(){
    return this.getStepAnswer("product-selection-deposits", "portfolio_builder['checkbox']")
  }

  _isSavingsPlansEnabled(){
    return this.getStepAnswer("product-selection-deposits", "savings_plan['checkbox']");
  }

  isEinzeltitelEnabled(){
    return this.getStepAnswer("product-selection-deposits", "einzeltitel['checkbox']")
  }

  isIndividualAssetManagementProcess(){
    return this.getBankConfig()['individual_asset_management'];
  }

}